import { useState, useEffect } from 'react';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  LockClosedIcon,
  ExclamationTriangleIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline';
import { Button, Input, Modal } from '@theraply/ui';
import {
  useJournalEntries,
  useJournalEntry,
  useCreateJournalEntry,
  useUpdateJournalEntry,
  useDeleteJournalEntry,
  type JournalEntryMeta,
} from '@theraply/api-client';
import {
  encryptNote,
  decryptNote,
  generateMasterKey,
  encryptMasterKeyWithPassword,
  decryptMasterKeyWithPassword,
} from '@theraply/crypto';

const JOURNAL_KEY_STORAGE = 'theraply_journal_key';

type View = 'list' | 'editor' | 'setup';

export default function JournalPage() {
  const [view, setView] = useState<View>('list');
  const [masterKey, setMasterKey] = useState<Uint8Array | null>(null);
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
  const [deletingEntryId, setDeletingEntryId] = useState<string | null>(null);

  // Check if encryption key exists in localStorage
  useEffect(() => {
    const storedKey = localStorage.getItem(JOURNAL_KEY_STORAGE);
    if (storedKey) {
      setView('list');
    } else {
      setView('setup');
    }
  }, []);

  const handleKeySetup = (key: Uint8Array) => {
    setMasterKey(key);
    setView('list');
  };

  const handleUnlock = (key: Uint8Array) => {
    setMasterKey(key);
  };

  if (view === 'setup') {
    return <KeySetup onSetup={handleKeySetup} />;
  }

  if (!masterKey) {
    return <UnlockJournal onUnlock={handleUnlock} />;
  }

  if (view === 'editor') {
    return (
      <JournalEditor
        entryId={editingEntryId}
        masterKey={masterKey}
        onBack={() => {
          setView('list');
          setEditingEntryId(null);
        }}
      />
    );
  }

  return (
    <JournalList
      masterKey={masterKey}
      onNewEntry={() => {
        setEditingEntryId(null);
        setView('editor');
      }}
      onEditEntry={(id) => {
        setEditingEntryId(id);
        setView('editor');
      }}
      deletingEntryId={deletingEntryId}
      onDeleteEntry={setDeletingEntryId}
      onCancelDelete={() => setDeletingEntryId(null)}
    />
  );
}

function KeySetup({ onSetup }: { onSetup: (key: Uint8Array) => void }) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    try {
      setIsCreating(true);
      const masterKey = await generateMasterKey();
      const encrypted = await encryptMasterKeyWithPassword(masterKey, password);
      localStorage.setItem(JOURNAL_KEY_STORAGE, encrypted);
      onSetup(masterKey);
    } catch (err) {
      setError('Failed to create encryption key. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900">Set Up Your Journal</h1>
      <p className="mt-1 text-sm text-gray-500">
        Your journal entries are encrypted with a password only you know. We never have access to your entries.
      </p>

      <div className="mt-8 max-w-md mx-auto">
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
              <LockClosedIcon className="h-6 w-6 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-medium text-gray-900">Create Journal Password</h2>
              <p className="text-sm text-gray-500">This encrypts your journal entries end-to-end.</p>
            </div>
          </div>

          {error && (
            <div className="mb-4 rounded-md bg-red-50 p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <form onSubmit={handleCreate} className="space-y-4">
            <Input
              type="password"
              label="Journal Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              required
              fullWidth
            />
            <Input
              type="password"
              label="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter password"
              required
              fullWidth
            />
            <div className="rounded-md bg-amber-50 p-3">
              <p className="text-sm text-amber-800">
                If you forget this password, your journal entries cannot be recovered.
                This password is separate from your account password.
              </p>
            </div>
            <Button type="submit" fullWidth isLoading={isCreating}>
              Create Encrypted Journal
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}

function UnlockJournal({ onUnlock }: { onUnlock: (key: Uint8Array) => void }) {
  const [password, setPassword] = useState('');
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const encryptedKey = localStorage.getItem(JOURNAL_KEY_STORAGE);
    if (!encryptedKey) {
      setError('No encryption key found. Please set up your journal first.');
      return;
    }

    try {
      setIsUnlocking(true);
      const masterKey = await decryptMasterKeyWithPassword(encryptedKey, password);
      onUnlock(masterKey);
    } catch (err) {
      setError('Incorrect password. Please try again.');
    } finally {
      setIsUnlocking(false);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900">Personal Journal</h1>
      <p className="mt-1 text-sm text-gray-500">
        Enter your journal password to unlock your encrypted entries.
      </p>

      <div className="mt-8 max-w-md mx-auto">
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
              <LockClosedIcon className="h-6 w-6 text-indigo-600" />
            </div>
            <h2 className="text-lg font-medium text-gray-900">Unlock Journal</h2>
          </div>

          {error && (
            <div className="mb-4 rounded-md bg-red-50 p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <form onSubmit={handleUnlock} className="space-y-4">
            <Input
              type="password"
              label="Journal Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your journal password"
              required
              fullWidth
            />
            <Button type="submit" fullWidth isLoading={isUnlocking}>
              Unlock
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}

function JournalList({
  masterKey,
  onNewEntry,
  onEditEntry,
  deletingEntryId,
  onDeleteEntry,
  onCancelDelete,
}: {
  masterKey: Uint8Array;
  onNewEntry: () => void;
  onEditEntry: (id: string) => void;
  deletingEntryId: string | null;
  onDeleteEntry: (id: string) => void;
  onCancelDelete: () => void;
}) {
  const { data: entries, isLoading, error, refetch } = useJournalEntries();
  const deleteMutation = useDeleteJournalEntry();

  const handleDelete = async () => {
    if (!deletingEntryId) return;
    try {
      await deleteMutation.mutateAsync(deletingEntryId);
      onCancelDelete();
    } catch (err) {
      console.error('Failed to delete journal entry:', err);
    }
  };

  if (isLoading) {
    return (
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Personal Journal</h1>
        <div className="mt-8 flex items-center justify-center min-h-64">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-indigo-600 border-r-transparent"></div>
            <p className="mt-4 text-gray-600">Loading journal entries...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Personal Journal</h1>
        <div className="mt-8 bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="text-red-800 font-semibold mb-2">Error Loading Journal</h3>
          <p className="text-red-600 text-sm">Failed to load your journal entries.</p>
          <button
            onClick={() => refetch()}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm font-medium"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Personal Journal</h1>
          <p className="mt-1 text-sm text-gray-500">
            Your encrypted journal entries. Only you can read them.
          </p>
        </div>
        <Button variant="primary" onClick={onNewEntry}>
          <PlusIcon className="h-5 w-5 mr-1" />
          New Entry
        </Button>
      </div>

      <div className="mt-8">
        {entries && entries.length > 0 ? (
          <div className="space-y-3">
            {entries.map((entry: JournalEntryMeta) => (
              <div
                key={entry.id}
                className="bg-white shadow rounded-lg p-5 hover:shadow-md transition-shadow cursor-pointer group"
                onClick={() => onEditEntry(entry.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-medium text-gray-900 truncate">
                      {entry.title || 'Untitled Entry'}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {new Date(entry.updatedAt).toLocaleDateString('en-US', {
                        weekday: 'long',
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditEntry(entry.id);
                      }}
                      className="p-2 text-gray-400 hover:text-indigo-600 rounded-md hover:bg-indigo-50 transition-colors"
                      title="Edit"
                    >
                      <PencilSquareIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteEntry(entry.id);
                      }}
                      className="p-2 text-gray-400 hover:text-red-600 rounded-md hover:bg-red-50 transition-colors"
                      title="Delete"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white shadow rounded-lg p-12 text-center">
            <BookOpenIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Journal Entries</h3>
            <p className="text-gray-600 mb-4">
              Start writing to record your thoughts and feelings.
            </p>
            <Button variant="primary" onClick={onNewEntry}>
              <PlusIcon className="h-5 w-5 mr-1" />
              Write Your First Entry
            </Button>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deletingEntryId !== null}
        onClose={onCancelDelete}
        title="Delete Journal Entry"
        size="sm"
      >
        <div>
          <p className="text-gray-600 mb-4">
            Are you sure you want to delete this entry? This action cannot be undone.
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onCancelDelete} disabled={deleteMutation.isPending}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} isLoading={deleteMutation.isPending}>
              Delete Entry
            </Button>
          </div>
          {deleteMutation.isError && (
            <p className="mt-3 text-sm text-red-600">
              Failed to delete entry. Please try again.
            </p>
          )}
        </div>
      </Modal>
    </div>
  );
}

function JournalEditor({
  entryId,
  masterKey,
  onBack,
}: {
  entryId: string | null;
  masterKey: Uint8Array;
  onBack: () => void;
}) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isDecrypting, setIsDecrypting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const { data: existingEntry, isLoading: entryLoading } = useJournalEntry(entryId);
  const createMutation = useCreateJournalEntry();
  const updateMutation = useUpdateJournalEntry();

  // Decrypt existing entry when loaded
  useEffect(() => {
    if (existingEntry && masterKey) {
      setIsDecrypting(true);
      setTitle(existingEntry.title || '');

      decryptNote(
        { ciphertext: existingEntry.encryptedContent, iv: existingEntry.iv, version: 1 },
        masterKey
      )
        .then((decrypted) => {
          setContent(decrypted);
        })
        .catch((err) => {
          setError('Failed to decrypt entry. Your encryption key may have changed.');
        })
        .finally(() => {
          setIsDecrypting(false);
        });
    }
  }, [existingEntry, masterKey]);

  const handleSave = async () => {
    if (!content.trim()) {
      setError('Entry content cannot be empty.');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      setSaveSuccess(false);

      // Encrypt the content
      const encrypted = await encryptNote(content, masterKey);

      if (entryId) {
        // Update existing entry
        await updateMutation.mutateAsync({
          id: entryId,
          encryptedContent: encrypted.ciphertext,
          iv: encrypted.iv,
          title: title || undefined,
        });
      } else {
        // Create new entry
        await createMutation.mutateAsync({
          encryptedContent: encrypted.ciphertext,
          iv: encrypted.iv,
          title: title || undefined,
        });
      }

      setSaveSuccess(true);
      setTimeout(() => {
        onBack();
      }, 500);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to save entry.');
    } finally {
      setIsSaving(false);
    }
  };

  const isLoading = entryLoading || isDecrypting;

  if (isLoading) {
    return (
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">
          {entryId ? 'Edit Entry' : 'New Entry'}
        </h1>
        <div className="mt-8 flex items-center justify-center min-h-64">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-indigo-600 border-r-transparent"></div>
            <p className="mt-4 text-gray-600">
              {isDecrypting ? 'Decrypting entry...' : 'Loading entry...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            {entryId ? 'Edit Entry' : 'New Entry'}
          </h1>
          <p className="mt-1 text-sm text-gray-500 flex items-center gap-1">
            <LockClosedIcon className="h-4 w-4" />
            End-to-end encrypted
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={onBack} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} isLoading={isSaving}>
            {entryId ? 'Save Changes' : 'Save Entry'}
          </Button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {saveSuccess && (
        <div className="mb-4 rounded-md bg-green-50 p-4">
          <p className="text-sm text-green-800">Entry saved successfully.</p>
        </div>
      )}

      <div className="bg-white shadow rounded-lg p-6 space-y-4">
        <Input
          type="text"
          label="Title (optional)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Give your entry a title..."
          fullWidth
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Content
          </label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={16}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm leading-relaxed"
            placeholder="Write your thoughts here..."
          />
        </div>
      </div>
    </div>
  );
}
